import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

type OrdersTab = 'licenses' | 'topups';

type ProductLicenseRow = {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  sku: string;
  edition: string;
  version: string;
  status: string;
  purchaseId: string | null;
  purchaseAmountCents: number | null;
  createdAt: string;
};

type TopupRow = {
  id: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  amountCents: number;
  creditsGranted: number;
  paymentIntent: string | null;
  status: string;
  createdAt: string;
};

type OrdersResponse = {
  rows: ProductLicenseRow[] | TopupRow[];
  total: number;
};

const PAGE_SIZE = 20;

const formatCurrency = (value: number | null | undefined) => {
  if (typeof value !== 'number') return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value / 100);
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusVariant = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === 'active' || normalized === 'succeeded') return 'default';
  if (normalized === 'pending' || normalized === 'processing') return 'secondary';
  if (normalized === 'failed' || normalized === 'canceled' || normalized === 'cancelled') return 'destructive';
  return 'outline';
};

export default function OrdersPanel() {
  const { session } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<OrdersTab>('licenses');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ProductLicenseRow[] | TopupRow[]>([]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, debouncedSearch, statusFilter, startDate, endDate]);

  const loadOrders = async () => {
    if (!session?.access_token) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-orders', {
        body: {
          tab: activeTab,
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          status: statusFilter.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      const payload = (data || { rows: [], total: 0 }) as OrdersResponse;
      setRows(payload.rows || []);
      setTotal(payload.total || 0);
    } catch (error: any) {
      setRows([]);
      setTotal(0);
      toast({
        title: 'Could not load orders',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders();
  }, [activeTab, page, debouncedSearch, statusFilter, startDate, endDate, session?.access_token]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(total, (page + 1) * PAGE_SIZE);

  const licenseRows = rows as ProductLicenseRow[];
  const topupRows = rows as TopupRow[];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Orders</CardTitle>
        <CardDescription>
          Review MapleOS product license purchases and AI credit top-ups with search, filters, and pagination.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as OrdersTab)}>
          <TabsList>
            <TabsTrigger value="licenses">Product Licenses</TabsTrigger>
            <TabsTrigger value="topups">AI Credit Top-ups</TabsTrigger>
          </TabsList>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search user name or email"
              className="xl:col-span-2"
            />
            <Input
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              placeholder="Status (optional)"
            />
            <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Showing {rangeStart}-{rangeEnd} of {total} {activeTab === 'licenses' ? 'licenses' : 'top-ups'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  setStatusFilter('');
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear filters
              </Button>
              <Button variant="outline" onClick={() => void loadOrders()} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          <TabsContent value="licenses" className="mt-0">
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Edition</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Purchase amount</TableHead>
                    <TableHead>Created date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!loading && licenseRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                        No product licenses matched your filters.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {licenseRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.userName || 'Unknown user'}</TableCell>
                      <TableCell>{row.userEmail || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{row.sku}</TableCell>
                      <TableCell>{row.edition}</TableCell>
                      <TableCell>{row.version}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>{formatCurrency(row.purchaseAmountCents)}</div>
                        {row.purchaseId ? <div className="text-xs text-muted-foreground">Purchase {row.purchaseId.slice(0, 8)}</div> : null}
                      </TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="topups" className="mt-0">
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Credits granted</TableHead>
                    <TableHead>Payment intent</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!loading && topupRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                        No AI credit top-ups matched your filters.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {topupRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.userName || 'Unknown user'}</TableCell>
                      <TableCell>{row.userEmail || '—'}</TableCell>
                      <TableCell>
                        <div>{formatCurrency(row.amountCents)}</div>
                        <Badge variant={statusVariant(row.status)} className="mt-1">{row.status}</Badge>
                      </TableCell>
                      <TableCell>{row.creditsGranted.toLocaleString()}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs" title={row.paymentIntent || undefined}>
                        {row.paymentIntent || '—'}
                      </TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Page {Math.min(page + 1, pageCount)} of {pageCount}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page === 0 || loading}>
              Previous
            </Button>
            <Button variant="outline" onClick={() => setPage((current) => (current + 1 < pageCount ? current + 1 : current))} disabled={page + 1 >= pageCount || loading}>
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
